import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useCartStore } from '../../store';

const ImageZoom = ({ src, alt, onClose }) =>
  ReactDOM.createPortal(
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <button
        onMouseDown={e => { e.stopPropagation(); onClose(); }}
        style={{ position: 'absolute', top: 18, right: 22, background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}
      >✕</button>
      <img
        src={src}
        alt={alt}
        onMouseDown={e => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 8px 48px rgba(0,0,0,0.6)', cursor: 'default' }}
      />
    </div>,
    document.body
  );

export const ProductCard = ({ product, onAddToCart }) => {
  const { items, addItem, updateQty, removeItem } = useCartStore();
  const [zoomed, setZoomed] = useState(false);
  const [added, setAdded] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const imgSrc = product.image_data || product.image_url;
  const outOfStock = product.stock_qty <= 0;

  const colors = product.colors
    ? product.colors.split(',').map(c => c.trim()).filter(Boolean)
    : [];
  const needsColor = colors.length > 0 && !selectedColor;

  const getColorQty = (c) => {
    const cKey = `prod-${product.id || product.product_id}-${c}`;
    const cItem = items.find(i => i._key === cKey);
    return cItem ? cItem.qty : 0;
  };

  const totalQty = colors.length > 0 
    ? colors.reduce((acc, c) => acc + getColorQty(c), 0)
    : (items.find(i => i._key === `prod-${product.id || product.product_id}`)?.qty || 0);

  const handleDec = () => {
    if (colors.length > 0) {
      // Find a color to decrement, prefer selectedColor
      let colorToDec = selectedColor && getColorQty(selectedColor) > 0 ? selectedColor : null;
      if (!colorToDec) colorToDec = colors.find(c => getColorQty(c) > 0);
      
      if (colorToDec) {
        const cKey = `prod-${product.id || product.product_id}-${colorToDec}`;
        const currentQty = getColorQty(colorToDec);
        if (currentQty > 1) {
          updateQty(cKey, currentQty - 1);
        } else if (currentQty === 1) {
          removeItem(cKey);
          if (selectedColor === colorToDec) setSelectedColor(colors.find(c => c !== colorToDec && getColorQty(c) > 0) || null);
        }
      }
    } else {
      const key = `prod-${product.id || product.product_id}`;
      const cartItem = items.find(i => i._key === key);
      if (cartItem && cartItem.qty > 1) {
        updateQty(key, cartItem.qty - 1);
      } else if (cartItem && cartItem.qty === 1) {
        removeItem(key);
      }
    }
  };

  const handleInc = (overrideColor = null) => {
    const colorToUse = overrideColor || selectedColor;
    if (outOfStock || (colors.length > 0 && !colorToUse)) return;
    
    addItem(product, 1, null, colorToUse);
    if (onAddToCart) onAddToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1000);
  };

  const handleColorClick = (c) => {
    const currentQty = getColorQty(c);
    if (currentQty > 0) {
      // Uncheck it
      removeItem(`prod-${product.id || product.product_id}-${c}`);
      if (selectedColor === c) {
        setSelectedColor(colors.find(color => color !== c && getColorQty(color) > 0) || null);
      }
    } else {
      // Check it
      setSelectedColor(c);
      handleInc(c);
    }
  };

  return (
    <div className="product-card">
      {zoomed && imgSrc && <ImageZoom src={imgSrc} alt={product.name} onClose={() => setZoomed(false)} />}
      <div
        className="product-card-img"
        onMouseDown={e => { if (imgSrc) { e.stopPropagation(); setZoomed(true); } }}
        style={{ cursor: imgSrc ? 'zoom-in' : 'default' }}
      >
        {imgSrc
          ? <img src={imgSrc} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
          : <span>📦</span>
        }
      </div>
      <div className="product-card-body">
        <div className="product-card-name">{product.name}</div>
        {product.brand && <div className="product-card-brand">{product.brand}</div>}
        <div className="product-card-price">${parseFloat(product.price).toFixed(2)}</div>
        {outOfStock && <span className="badge badge-danger mt-1">Out of Stock</span>}
        {!outOfStock && product.stock_qty <= 5 && <span className="badge badge-warning mt-1">Only {product.stock_qty} left</span>}
        {colors.length > 0 && (
          <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {colors.map(c => (
              <button
                key={c}
                onClick={() => handleColorClick(c)}
                title={c}
                style={{
                  border: getColorQty(c) > 0 ? '2px solid #2563eb' : '2px solid #e5e7eb',
                  borderRadius: 999,
                  padding: '0.1rem 0.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: getColorQty(c) > 0 ? '#dbeafe' : '#f9fafb',
                  color: getColorQty(c) > 0 ? '#1e40af' : '#374151',
                  boxShadow: selectedColor === c && colors.length > 1 ? '0 0 0 2px #93c5fd' : 'none',
                  transition: 'all 0.15s',
                }}
              >{c}{getColorQty(c) > 1 ? ` (${getColorQty(c)})` : ''}</button>
            ))}
          </div>
        )}
        {colors.length > 0 && !selectedColor && !outOfStock && (
          <div style={{ fontSize: '0.68rem', color: '#f59e0b', marginTop: '0.25rem', fontWeight: 600 }}>Select a color</div>
        )}
      </div>
      <div className="product-card-footer">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <button
            className="qty-btn"
            onClick={handleDec}
            disabled={outOfStock || totalQty === 0}
            title="Decrease"
            style={{ width: 32, height: 32, fontSize: '1.2rem' }}
          >−</button>
          <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700, fontSize: '1rem' }}>{totalQty}</span>
          <button
            className="qty-btn"
            onClick={() => handleInc()}
            disabled={outOfStock || needsColor}
            title={needsColor ? 'Select a color first' : 'Add to cart'}
            style={{ width: 32, height: 32, fontSize: '1.2rem', background: added ? '#10b981' : undefined, color: added ? '#fff' : undefined }}
          >{added ? '✓' : '+'}</button>
        </div>
        {added && <div style={{ textAlign: 'center', fontSize: '0.72rem', color: '#10b981', marginTop: '0.2rem', fontWeight: 600 }}>Added!</div>}
      </div>
    </div>
  );
};

export const LoadingSpinner = () => <div className="spinner" />;

export const EmptyState = ({ icon = '📭', title, message }) => (
  <div className="text-center" style={{ padding: '3rem 1rem' }}>
    <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{icon}</div>
    {title && <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.3rem' }}>{title}</div>}
    {message && <div className="text-muted text-sm">{message}</div>}
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    placed: 'badge-info', inprogress: 'badge-warning',
    shipped: 'badge-primary', delivered: 'badge-success',
    cancelled: 'badge-danger',
    pending: 'badge-gray', paid: 'badge-success',
    failed: 'badge-danger', refunded: 'badge-warning',
  };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
};
